import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { authStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert } from '@/components/ui/alert';

const JoinOrganizationPage = observer(() => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string>('');
  const [invitedEmail, setInvitedEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [name, setName] = useState<string>('');

  useEffect(() => {
    const verifyInvite = async () => {
      try {
        if (token) {
          const inviteData = await authStore.verifyOrganizationInvite(token);
          setOrgName(inviteData.organizationName);
          setInvitedEmail(inviteData.email);
          
          if (authStore.isAuthenticated) {
            // If user is already logged in, join the organization directly
            await authStore.acceptOrganizationInvite(token);
            navigate('/dashboard', { replace: true });
          } else {
            // If not logged in, show join options
            setLoading(false);
          }
        }
      } catch (error) {
        setError('Invalid or expired invitation');
        setLoading(false);
      }
    };

    verifyInvite();
  }, [token, navigate]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await authStore.register(invitedEmail, password, name);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      // Error handled by store
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-6">
          <Alert variant="destructive">{error}</Alert>
          <Button className="mt-4 w-full" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6">
        <h1 className="text-2xl font-bold mb-4">Join {orgName}</h1>
        <p className="text-gray-600 mb-6">
          You've been invited to join {orgName}. Please choose an option below:
        </p>
        <div className="space-y-4">
          <Button
            className="w-full"
            onClick={() => navigate('/login', { 
              state: { returnTo: `/join-organization/${token}` } 
            })}
          >
            Login to Join
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate('/register', { 
              state: { returnTo: `/join-organization/${token}` } 
            })}
          >
            Create Account
          </Button>
        </div>
      </Card>
    </div>
  );
});

export default JoinOrganizationPage; 